import React, { useEffect, useState } from 'react';
import axios from 'axios';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import './ProfessionalReport.css';

function ProfessionalReport() {
  const [weeklyData, setWeeklyData] = useState([]);
  const [monthlyData, setMonthlyData] = useState([]);
  const [allEmployees, setAllEmployees] = useState([]);
  const [employeeTipoFilter, setEmployeeTipoFilter] = useState('All');
  const [allEmployeeTypes, setAllEmployeeTypes] = useState([]);
  const [view, setView] = useState('weekly');
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [weeklyResponse, monthlyResponse] = await Promise.all([
          axios.get('http://192.168.0.126:5000/weekly-intervals'),
          axios.get('http://192.168.0.126:5000/monthly-intervals'),
        ]);

        const normalize = data =>
          data.map(emp => ({
            ...emp,
            intervals: emp.intervals.map(i => ({
              ...i,
              duration_in_hours:
                typeof i.duration_in_hours === 'number'
                  ? i.duration_in_hours
                  : parseFloat(i.duration_in_hours) || 0,
            })),
          }));

        const weekly = normalize(weeklyResponse.data);
        const monthly = normalize(monthlyResponse.data);

        setWeeklyData(weekly);
        setMonthlyData(monthly);

        const combined = [...weekly, ...monthly];
        const map = new Map();
        combined.forEach(emp => {
          if (!map.has(emp.employee_code)) {
            map.set(emp.employee_code, {
              employee_code: emp.employee_code,
              employee_name: emp.employee_name,
              employee_tipo: emp.employee_tipo,
            });
          }
        });
        const unique = Array.from(map.values());
        setAllEmployees(unique);
        setAllEmployeeTypes(
          Array.from(new Set(unique.map(e => e.employee_tipo)))
        );

        setLoading(false);
      } catch (err) {
        console.error(err);
        setError('Error al cargar los datos.');
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const maxGapMinutes = 240;

  const determineShiftType = startTime => {
    if (!startTime) return 'Desconocido';
    const [, time] = startTime.split(' ');
    const [hour, minute] = time.split(':').map(Number);
    const totalMinutes = hour * 60 + minute;
    if (totalMinutes < 375) return 'Mañana';
    if (totalMinutes < 855) return 'Tarde';
    return 'Noche';
  };

  const isCompliant = intervals => {
    if (intervals.length === 0) return false;
    const first = intervals[0];
    const shift = determineShiftType(first.start_time);
    let limit;
    if (shift === 'Mañana') limit = 375;
    else if (shift === 'Tarde') limit = 855;
    else limit = 1275;
    const [h, m] = first.start_time.split(' ')[1].split(':').map(Number);
    const enteredOnTime = h * 60 + m <= limit;
    const totalH = intervals.reduce(
      (sum, i) => sum + i.duration_in_hours,
      0
    );
    const hoursValid = totalH <= 12;
    const allValid = intervals.every(i => i.duration_in_hours > 1);
    return enteredOnTime && hoursValid && allValid;
  };

  const exportToPDF = () => {
    const pdf = new jsPDF('portrait', 'mm', 'a4');
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const margin = 15;
    let yOffset = margin;

    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(18);
    pdf.text(
      'Reporte de Horarios de Empleados',
      pageWidth / 2,
      yOffset,
      { align: 'center' }
    );
    yOffset += 10;

    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(10);
    pdf.text(
      `Fecha: ${new Date().toLocaleDateString('es-ES')}`,
      pageWidth - margin,
      yOffset,
      { align: 'right' }
    );
    yOffset += 8;

    pdf.setLineWidth(0.5);
    pdf.line(margin, yOffset, pageWidth - margin, yOffset);
    yOffset += 5;

    allEmployees.forEach(employee => {
      if (
        employeeTipoFilter !== 'All' &&
        employee.employee_tipo !== employeeTipoFilter
      ) {
        return;
      }
      if (!employee.employee_name.toLowerCase().includes(searchTerm)) {
        return;
      }

      const currentData = view === 'weekly' ? weeklyData : monthlyData;
      const employeeData =
        currentData.find(
          emp => emp.employee_code === employee.employee_code
        ) || null;

      pdf.setFillColor(230, 230, 250);
      pdf.rect(margin, yOffset, pageWidth - 2 * margin, 10, 'F');

      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(12);
      pdf.setTextColor(0, 0, 128);
      pdf.text(
        `Empleado: ${employee.employee_name} - Tipo: ${employee.employee_tipo}`,
        margin + 3,
        yOffset + 7
      );
      yOffset += 12;

      if (employeeData && employeeData.intervals.length > 0) {
        const groups = employeeData.intervals.reduce((acc, interval) => {
          const date = interval.start_time.split(' ')[0];
          if (!acc[date]) acc[date] = [];
          acc[date].push(interval);
          return acc;
        }, {});
        const sortedDates = Object.keys(groups).sort(
          (a, b) => new Date(b) - new Date(a)
        );

        sortedDates.forEach(date => {
          const intervals = groups[date];
          const compliance = isCompliant(intervals);
          const sortedIntervals = [...intervals].sort(
            (a, b) =>
              new Date(a.start_time) - new Date(b.start_time)
          );

          let breakTime = '';
          let breakDiffHours = 0;
          if (sortedIntervals.length >= 2) {
            const firstExit = sortedIntervals[0].end_time;
            const secondEntry = sortedIntervals[1].start_time;
            const diffMs =
              new Date(secondEntry) - new Date(firstExit);
            if (diffMs > 0) {
              breakDiffHours = parseFloat(
                (diffMs / 3600000).toFixed(2)
              );
              const diffMins = Math.floor(diffMs / 60000);
              const hours = Math.floor(diffMins / 60);
              const mins = diffMins % 60;
              breakTime = `${hours}h ${mins}m`;
            }
          }

          pdf.setFont('helvetica', 'bold');
          pdf.setFontSize(11);
          pdf.setTextColor(0, 0, 0);
          pdf.text(
            `${new Date(date).toLocaleDateString('es-ES')} - ${
              compliance ? '✅ Cumple' : '❌ No cumple'
            }`,
            margin,
            yOffset
          );
          yOffset += 6;

          const body = sortedIntervals.map((interval, index) => {
            const startHour = interval.start_time
              .split(' ')[1]
              .substring(0, 5);
            const endHour = interval.end_time
              .split(' ')[1]
              .substring(0, 5);
            let descanso = '';
            if (index === 1 && breakTime) {
              descanso = breakTime;
            }
            return [
              startHour,
              endHour,
              interval.duration_in_hours
                ? interval.duration_in_hours.toFixed(2)
                : '-',
              descanso,
            ];
          });

          pdf.autoTable({
            startY: yOffset,
            head: [['Inicio', 'Fin', 'Duración (h)', 'Descanso']],
            body,
            margin: { left: margin, right: margin },
            styles: { fontSize: 9 },
            headStyles: { fillColor: [240, 240, 240] },
          });

          yOffset = pdf.lastAutoTable.finalY + 8;

          if (yOffset > pageHeight - margin - 20) {
            pdf.addPage();
            yOffset = margin;
          }
        });
      } else {
        pdf.setFont('helvetica', 'italic');
        pdf.setFontSize(10);
        pdf.setTextColor(255, 0, 0);
        pdf.text(
          'No hay registros para este período.',
          margin + 3,
          yOffset
        );
        yOffset += 8;
      }

      pdf.setLineWidth(0.3);
      pdf.setDrawColor(200);
      pdf.line(margin, yOffset, pageWidth - margin, yOffset);
      yOffset += 8;

      if (yOffset > pageHeight - margin - 20) {
        pdf.addPage();
        yOffset = margin;
      }
    });

    pdf.save('reporte_empleados_nuevo.pdf');
  };

  if (loading) return <p className="pr-loading">Cargando datos...</p>;
  if (error) return <p className="pr-error">{error}</p>;

  return (
    <div className="pr-container">
      <div className="pr-controls">
        <div className="pr-buttons-group">
          <button
            onClick={() => setView('weekly')}
            className={
              view === 'weekly' ? 'pr-btn active' : 'pr-btn'
            }
          >
            Ver Semanal
          </button>
          <button
            onClick={() => setView('monthly')}
            className={
              view === 'monthly' ? 'pr-btn active' : 'pr-btn'
            }
          >
            Ver Mensual
          </button>
          <button className="pr-btn" onClick={exportToPDF}>
            Exportar a PDF
          </button>
        </div>

        <div className="pr-employee-type-filters">
          <button
            onClick={() => setEmployeeTipoFilter('All')}
            className={
              employeeTipoFilter === 'All'
                ? 'pr-filter-btn active'
                : 'pr-filter-btn'
            }
          >
            Todos
          </button>
          {allEmployeeTypes.map(tipo => (
            <button
              key={tipo}
              onClick={() => setEmployeeTipoFilter(tipo)}
              className={
                employeeTipoFilter === tipo
                  ? 'pr-filter-btn active'
                  : 'pr-filter-btn'
              }
            >
              {tipo}
            </button>
          ))}
        </div>

        <input
          type="text"
          placeholder="Buscar empleado..."
          value={searchTerm}
          onChange={e =>
            setSearchTerm(e.target.value.toLowerCase())
          }
          className="pr-search-input"
        />
      </div>

      <div className="pr-report-content">
        {allEmployees
          .filter(emp =>
            emp.employee_name
              .toLowerCase()
              .includes(searchTerm)
          )
          .map((emp, idx) => {
            if (
              employeeTipoFilter !== 'All' &&
              emp.employee_tipo !== employeeTipoFilter
            )
              return null;

            const data =
              (view === 'weekly' ? weeklyData : monthlyData).find(
                d => d.employee_code === emp.employee_code
              ) || { intervals: [] };

            const intervals = data.intervals;

            const groups = Object.entries(
              intervals.reduce((g, i) => {
                const date = i.start_time.split(' ')[0];
                g[date] = g[date] || [];
                g[date].push(i);
                return g;
              }, {})
            ).sort((a, b) => new Date(b[0]) - new Date(a[0]));

            let prevLast = null;

            return (
              <div
                key={emp.employee_code}
                className={`pr-employee-section ${
                  idx % 2 === 0
                    ? 'pr-black-border'
                    : 'pr-gray-border'
                }`}
              >
                <h3>
                  {emp.employee_name} ({emp.employee_tipo})
                </h3>
                {groups.map(([date, ints]) => {
                  const sorted = [...ints].sort(
                    (a, b) =>
                      new Date(a.start_time) -
                      new Date(b.start_time)
                  );
                  const compliant = isCompliant(sorted);

                  let totalSum = null;
                  let breakTime = '';

                  if (prevLast) {
                    const diffPrev =
                      new Date(sorted[0].start_time) -
                      new Date(prevLast.end_time);
                    const gapPrevMins = diffPrev / 60000;
                    if (
                      gapPrevMins > 0 &&
                      gapPrevMins <= maxGapMinutes
                    ) {
                      totalSum = (
                        prevLast.duration_in_hours +
                        sorted[0].duration_in_hours
                      ).toFixed(2);
                    }
                  }
                  if (!totalSum && sorted.length >= 2) {
                    totalSum = (
                      sorted[0].duration_in_hours +
                      sorted[1].duration_in_hours
                    ).toFixed(2);
                    const diff =
                      new Date(sorted[1].start_time) -
                      new Date(sorted[0].end_time);
                    if (diff > 0) {
                      const mins = Math.floor(diff / 60000);
                      breakTime = `${Math.floor(
                        mins / 60
                      )}h ${mins % 60}m`;
                    }
                  }
                  prevLast =
                    sorted[sorted.length - 1] || prevLast;

                  return (
                    <div
                      key={date}
                      className="pr-day-section"
                    >
                      <table className="pr-table">
                        <thead>
                          <tr>
                            <th>Fecha</th>
                            <th>Inicio</th>
                            <th>Fin</th>
                            <th>Duración (h)</th>
                            <th>Descanso</th>
                            <th>
                              {compliant
                                ? '✅ Cumple'
                                : '❌ No cumple'}
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {sorted.map((i, j) => (
                            <tr key={j}>
                              <td>
                                {new Date(
                                  i.start_time.split(' ')[0]
                                ).toLocaleDateString('es-ES')}
                              </td>
                              <td>
                                {i.start_time
                                  .split(' ')[1]
                                  .substring(0, 5)}
                              </td>
                              <td>
                                {i.end_time
                                  .split(' ')[1]
                                  .substring(0, 5)}
                              </td>
                              <td>
                                {i.duration_in_hours.toFixed(2)}
                              </td>
                              <td
                                style={{
                                  color:
                                    j === 1 && breakTime
                                      ? 'red'
                                      : 'black',
                                }}
                              >
                                {j === 1 ? breakTime : ''}
                              </td>
                              <td>
                                {j === 0 ? (
                                  compliant ? (
                                    <span className="pr-compliant">
                                      ✅
                                    </span>
                                  ) : (
                                    <span className="pr-non-compliant">
                                      ❌
                                    </span>
                                  )
                                ) : null}
                              </td>
                            </tr>
                          ))}
                          {totalSum && (
                            <tr className="pr-total-row">
                              <td colSpan={3}>
                                <strong>
                                  Total Trabajado:
                                </strong>
                              </td>
                              <td colSpan={3}>
                                <strong>
                                  {totalSum} hrs
                                </strong>
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  );
                })}
              </div>
            );
          })}
      </div>
    </div>
  );
}

export default ProfessionalReport;
